import { useState, useCallback, useEffect, useMemo } from 'react';
import { EventData, EVENTS, STATUS, Step } from 'react-joyride';

const TOUR_KEY = 'solimat-delta-search-tour-v1';

const BASE_STEPS: Step[] = [
  {
    target: '.delta-search-layout',
    title: 'Buscador de accidentes DELT@',
    content: 'Esta pantalla está dividida en dos áreas: el panel de búsqueda (izquierda) donde introduces los filtros, y el área de resultados (derecha) donde aparecen los expedientes encontrados.',
    placement: 'top',
    skipBeacon: true,
  },
  {
    target: '.delta-search-sidebar',
    title: 'Panel de búsqueda',
    content: 'Aquí encontrarás los filtros disponibles para buscar expedientes. Combina varios criterios para acotar los resultados.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '.delta-buscador-body',
    title: 'Filtros de búsqueda',
    content: 'Rellena los campos que necesites: fechas, trabajador, empresa, estado de validación, etc. No es obligatorio completar todos los filtros.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '.delta-buscador-actions',
    title: 'Buscar y limpiar',
    content: 'Pulsa "Buscar" para lanzar la consulta con los filtros introducidos. Usa "Borrar" para restablecer todos los campos y comenzar una nueva búsqueda.',
    placement: 'top',
    skipBeacon: true,
  },
  {
    target: '.delta-search-content',
    title: 'Área de resultados',
    content: 'Los expedientes que coincidan con tus filtros aparecerán en esta zona.',
    placement: 'left',
    skipBeacon: true,
  },
];

const RESULT_STEPS: Step[] = [
  {
    target: '.delta-result-filters',
    title: 'Filtros de resultados',
    content: 'Una vez obtenidos resultados, puedes filtrarlos rápidamente por estado: Validados, Sin validar, Remesados o Sin remesar.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '.delta-result-grid',
    title: 'Lista de expedientes',
    content: 'Aquí se muestran todos los expedientes que coinciden con tu búsqueda, organizados en tarjetas.',
    placement: 'top',
    skipBeacon: true,
  },
  {
    target: '.delta-result-card',
    title: 'Tarjeta de expediente',
    content: 'Cada tarjeta muestra los datos principales del accidente: trabajador, empresa, fechas y su estado de validación y remesa.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '.delta-result-card__header',
    title: 'Estado del expediente',
    content: 'Los iconos en la cabecera indican si el expediente está validado (✓/✗) y si ya ha sido remesado a la mutua.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '.delta-result-card__footer',
    title: 'Acceder al expediente',
    content: 'Pulsa "Editar" para modificar el formulario o "Ver" para consultarlo si ya ha sido remesado. También puedes marcarlo para remesar.',
    placement: 'top',
    skipBeacon: true,
  },
];

export const useDeltaSearchTour = (hasResults: boolean = false) => {
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  const steps = useMemo<Step[]>(
    () => (hasResults ? [...BASE_STEPS, ...RESULT_STEPS] : BASE_STEPS),
    [hasResults],
  );

  const handleCallback = useCallback((data: EventData) => {
    const { type, status, index } = data as any;
    const finished = status === STATUS.FINISHED || status === STATUS.SKIPPED;

    if (finished || (type as string) === EVENTS.TOUR_END) {
      setRun(false);
      localStorage.setItem(TOUR_KEY, 'true');
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }

    if ((type as string) === EVENTS.STEP_AFTER && index === 0) {
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }
  }, []);

  const startTour = useCallback(() => {
    setRun(current => {
      if (current) return false;
      setTourKey(k => k + 1);
      return true;
    });
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: run } }));
  }, [run]);

  return { run, tourKey, steps, handleCallback, startTour };
};
